import { createContext, type PropsWithChildren } from "react";

import { type Step, type WizardContextDescriptor } from "./types";
import { getActiveStepIndex } from "./utils";

export const WizardContext = createContext<WizardContextDescriptor<any> | null>(
  null,
);

interface Props<T> {
  steps: Step<T>[];
  active: string;
  disabled?: boolean;
  onChange: (step: T) => void;
}

export const WizardProvider = <T extends string>({
  steps,
  active,
  disabled,
  children,
  onChange,
}: PropsWithChildren<Props<T>>) => {
  const activeStepIndex = getActiveStepIndex(active, steps);

  const prevStep = activeStepIndex === 0 ? null : steps[activeStepIndex - 1];
  const nextStep =
    activeStepIndex > steps.length - 1 ? null : steps[activeStepIndex + 1];

  const navigateToPreviousStep = () => {
    if (!prevStep) {
      return;
    }
    onChange(prevStep.value);
  };

  const navigateToNextStep = () => {
    if (!nextStep) {
      return;
    }
    onChange(nextStep.value);
  };

  return (
    <WizardContext.Provider
      value={{
        disabled,
        steps,
        active,
        activeStepIndex,
        prevStep,
        nextStep,
        onChange,
        navigateToPreviousStep,
        navigateToNextStep,
      }}
    >
      {children}
    </WizardContext.Provider>
  );
};
